import { createSharedComposable } from '@vueuse/core'

/**
 * @description
 * 1. 监听容器高度变化，变化时更新 intercom 位置
 * 2. 监听 intercom 元素的渲染完成及变化
 * 3. 实时响应容器高度变化
 *
 * @returns {{ observe }} observe 方法，用于监听容器高度变化，并更新 intercom 位置
 */
export const useIntercomObserver = createSharedComposable(() => {
  let intercomObserver

  const bottom = ref(12)

  /**
   * 监听容器高度是否发生变化，若变化则触发 intercom 位置调整
   */
  const observer = new ResizeObserver((entries) => {
    entries.forEach(entry => {
      const rect = entry.target.getBoundingClientRect()
      bottom.value = rect.top ? window.innerHeight - rect.top : 0
    })
  })

  watch(bottom, () => {
    updateIntercomStyle()
  })

  function getIntercomElement () {
    return document.querySelector('.intercom-launcher') ?? document.querySelector('[class^="intercom-with-namespace"]')
  }

  function updateIntercomStyle () {
    const el = getIntercomElement()
    if (el) {
      el.style.transition = 'all .3s ease-in-out'
      // 若 top 为 0 则默认为初始化，将其高度调整为据底部 12px
      if (top.value === 0) {
        el.style.bottom = '12px'
        el.style.right = '12px'
      } else {
        el.style.bottom = `${bottom.value + 12}px`
        el.style.right = '12px'
      }
      el.style.zIndex = 1100
      el.style.transformOrigin = 'right bottom'
      el.style.transform = 'scale(0.8)'
      el.style.animation = 'none'
    }
  }

  /**
   * 监听 intercom 元素是否渲染完成及发生变化
   */
  function watchIntercom () {
    /**
     * 判断是否是 intercom 元素变化，如果不是则不触发 updateIntercomStyle
     */
    const observer = new MutationObserver((mutationsList) => {
      for (const mutation of mutationsList) {
        if (mutation.type === 'childList' && mutation.addedNodes.length > 0) {
          for (const el of mutation.addedNodes) {
            if (el instanceof HTMLElement && el.matches('.intercom-launcher, [class^="intercom-with-namespace"]')) {
              updateIntercomStyle()
              break
            }
          }
        }
      }
    })

    // 开始观察整个document及其子元素的变化
    observer.observe(document, {
      childList: true,
      subtree: true
    })

    // 立即检查一次，可能已经存在
    const existingElement = getIntercomElement()
    if (existingElement) {
      updateIntercomStyle()
    }

    return observer
  }

  onMounted(() => {
    if (!intercomObserver) {
      intercomObserver = watchIntercom()
    }
  })

  onBeforeUnmount(() => {
    if (intercomObserver) {
      intercomObserver.disconnect()
      intercomObserver = null
    }
  })

  const observe = (el) => {
    observer.observe(el)
  }

  return {
    observe
  }
})
